import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../config/api-config';
import './BoardComponents.css';

const Task = ({ id, initialTasks = [{ id: 1, text: '', completed: false }], initialPosition = { x: 100, y: 100 }, initialSize = { width: 200, height: 200 }, onUpdate, onDelete, onDragStart, onDragMove, onDragEnd }) => {
  const { boardId } = useParams(); // Get boardId from URL params
  const [tasks, setTasks] = useState(initialTasks);
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartSize, setResizeStartSize] = useState({ width: 0, height: 0 });
  const taskRef = useRef(null);

  // Handle dragging with improved offset calculation
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('task-header') || e.target.parentElement.classList.contains('task-header')) {
      setIsDragging(true);
      
      // Notify parent component that dragging has started
      if (onDragStart) {
        onDragStart(id);
      }
      
      // Get the current position of the task
      const rect = taskRef.current
      
      // Calculate the offset from the mouse position to the top-left corner of the task
      // This ensures the task doesn't jump to have its corner at the mouse position
      setDragOffset({
        x: rect.offsetLeft - e.clientX,
        y: rect.offsetTop - e.clientY
      });
      
      // Prevent text selection during drag
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      // Get the board container to calculate relative position
      const boardContent = document.querySelector('.board-content');
      const boardRect = boardContent.getBoundingClientRect();
      
      // Calculate new position relative to the board container
      // Add the drag offset to maintain the same relative position to the mouse
      const newX = e.clientX + dragOffset.x;
      const newY = e.clientY + dragOffset.y;
      
      // Apply constraints to keep the task within the board
      const constrainedX = Math.max(0, Math.min(newX, boardRect.width - taskRef.current.offsetWidth));
      const constrainedY = Math.max(0, Math.min(newY, boardRect.height - taskRef.current.offsetHeight));
      
      const newPosition = { x: constrainedX, y: constrainedY };
      setPosition(newPosition);
      
      // Notify parent component that dragging is in progress
      // This updates the timestamp on every frame during dragging
      if (onDragMove) {
        onDragMove(id);
      }
      
      // We're not updating position in real-time during dragging anymore
      // Only when mouse is released (handleMouseUp) to reduce server load
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      setIsDragging(false);
      
      // Final update when drag is complete to ensure position is saved
      // Only update for non-temporary components
      if (onUpdate && !id.toString().startsWith('temp-')) {
        // Get the current position from the DOM element
        // This ensures we use the actual final position
        const rect = taskRef.current.getBoundingClientRect();
        const boardContent = document.querySelector('.board-content');
        const boardRect = boardContent.getBoundingClientRect();
        
        // Calculate the position relative to the board
        const finalPosition = {
          x: e.clientX + dragOffset.x,
          y: e.clientY + dragOffset.y
        };
        
        console.log('Updating position on drag end:', finalPosition);
        
        setPosition(finalPosition);
        // Notify parent component that dragging has ended and pass the final position
        if (onDragEnd) {
          onDragEnd(finalPosition);
        }
        
        // Update via the special position endpoint for better persistence
        if (boardId) {
          const updatePositionInBoard = async () => {
            try {
              // Import auth functions
              const { getIdToken } = await import('firebase/auth');
              const { auth } = await import('../../config/firebase-config');
              
              // Get current user token
              const token = await getIdToken(auth.currentUser);
              
              // Update position via API with axios
              await api.put(
                `/api/boards/${boardId}/components/${id}/position`,
                { position: finalPosition },
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              console.log('Position updated via dedicated endpoint successfully:', finalPosition);
            } catch (err) {
              console.error('Error updating position via endpoint:', err);
              
              // Fallback to regular update through parent component
              console.log('Falling back to regular update method');
              onUpdate(id, { position: finalPosition });
              
            }
          };
          
          updatePositionInBoard();
        } else {
          // If no boardId, use regular update
          onUpdate(id, { position: finalPosition });
          
          // Update the local state to match the sent position
          // Use setTimeout to ensure this happens after the current execution context
          setTimeout(() => {
            setPosition(finalPosition);
          }, 0);
        }
      }
    }
  };

  // Handle task changes with shorter debouncing for more real-time updates
  const handleTaskChange = (taskId, field, value) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId ? { ...task, [field]: value } : task
    );
    setTasks(updatedTasks);
    
    // Only update server for non-temporary components
    if (id && boardId && !id.toString().startsWith('temp-')) {
      // Clear any existing timeout
      if (taskRef.current.tasksUpdateTimeout) {
        clearTimeout(taskRef.current.tasksUpdateTimeout);
      }
      
      // Set a new timeout with shorter delay
      taskRef.current.tasksUpdateTimeout = setTimeout(async () => {
        try {
          console.log('Sending tasks update to server:', updatedTasks);
          
          // Get auth token
          const { getIdToken } = await import('firebase/auth');
          const { auth } = await import('../../config/firebase-config');
          const token = await getIdToken(auth.currentUser);
          
          // Send update via API
          await api.put(
            `/api/boards/${boardId}/components/${id}/tasks`,
            { tasks: updatedTasks },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Tasks updated via API successfully');
          // We don't need to call onUpdate here since we're using the dedicated endpoint
          // This avoids potential conflicts between the two update methods
        } catch (err) {
          console.error('Error updating tasks via API:', err);
          
          // Fallback to regular update only if the dedicated endpoint fails
          if (onUpdate) {
            onUpdate(id, { tasks: updatedTasks });
          }
        }
        
        taskRef.current.tasksUpdateTimeout = null;
      }, 100); // 100ms debounce for more responsive updates
    }
  };

  // Add new task with immediate update
  const addTask = () => {
    const newTask = {
      id: Date.now(), // Use timestamp as unique ID
      text: '',
      completed: false
    };
    
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    
    // Only send updates for non-temporary components
    if (id && boardId && !id.toString().startsWith('temp-')) {
      // Clear any existing timeout
      if (taskRef.current.tasksUpdateTimeout) {
        clearTimeout(taskRef.current.tasksUpdateTimeout);
      }
      
      // Send update immediately for adding tasks
      (async () => {
        try {
          console.log('Sending tasks update after adding task:', updatedTasks);
          
          // Get auth token
          const { getIdToken } = await import('firebase/auth');
          const { auth } = await import('../../config/firebase-config');
          const token = await getIdToken(auth.currentUser);
          
          // Send update via API
          await api.put(
            `/api/boards/${boardId}/components/${id}/tasks`,
            { tasks: updatedTasks },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Tasks updated after adding task via API successfully');
          // We don't need to call onUpdate here since we're using the dedicated endpoint
          // This avoids potential conflicts between the two update methods
        } catch (err) {
          console.error('Error updating tasks after adding task via API:', err);
          
          // Fallback to regular update only if the dedicated endpoint fails
          if (onUpdate) {
            onUpdate(id, { tasks: updatedTasks });
          }
        }
      })();
    }
  };
  // Remove task
  const removeTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);
    
    // Only send updates for non-temporary components
    if (id && boardId && !id.toString().startsWith('temp-')) {
      // For removal, update immediately
      (async () => {
        try {
          console.log('Sending tasks update after removing task:', updatedTasks);
          
          // Get auth token
          const { getIdToken } = await import('firebase/auth');
          const { auth } = await import('../../config/firebase-config');
          const token = await getIdToken(auth.currentUser);
          
          // Send update via API
          await api.put(
            `/api/boards/${boardId}/components/${id}/tasks`,
            { tasks: updatedTasks },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Tasks updated after removing task via API successfully');
          // We don't need to call onUpdate here since we're using the dedicated endpoint
          // This avoids potential conflicts between the two update methods
        } catch (err) {
          console.error('Error updating tasks after removing task via API:', err);
          
          // Fallback to regular update only if the dedicated endpoint fails
          if (onUpdate) {
            onUpdate(id, { tasks: updatedTasks });
          }
        }
      })();
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  // Track if position was manually changed during dragging
  const positionManuallyChanged = useRef(false);

  // Effect to update position when initialPosition changes
  useEffect(() => {
    // Only update position from initialPosition if not currently dragging
    if (initialPosition && !isDragging) {
      setPosition(initialPosition);
    }
  }, [initialPosition, isDragging]);
  
  // Effect to update tasks when initialTasks changes
  useEffect(() => {
    if (initialTasks && initialTasks.length > 0) {
      setTasks(initialTasks);
    }
  }, [initialTasks]);

  // Handle resize start
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    
    // Store the initial mouse position
    setResizeStartPos({
      x: e.clientX,
      y: e.clientY
    });
    
    // Store the initial size
    setResizeStartSize({
      width: taskRef.current.offsetWidth,
      height: taskRef.current.offsetHeight
    });
  };
  
  // Handle resize move
  const handleResizeMove = (e) => {
    if (!isResizing) return;
    
    // Calculate the change in position
    const deltaX = e.clientX - resizeStartPos.x;
    const deltaY = e.clientY - resizeStartPos.y;
    
    // Calculate new size
    const newWidth = Math.max(200, resizeStartSize.width + deltaX); // Minimum width of 200px
    const newHeight = Math.max(100, resizeStartSize.height + deltaY); // Minimum height of 100px
    
    // Update size state
    setSize({
      width: newWidth,
      height: newHeight
    });
    
    // For immediate visual feedback, directly update the DOM element's style
    if (taskRef.current) {
      // Update the container size
      taskRef.current.style.width = `${newWidth}px`;
      taskRef.current.style.height = `${newHeight}px`;
      
      // Update the task content container to fit within the container
      const taskContent = taskRef.current.querySelector('.task-content');
      if (taskContent) {
        // Set the height to be the container height minus the header height
        const headerHeight = taskRef.current.querySelector('.task-header').offsetHeight;
        taskContent.style.height = `${newHeight - headerHeight}px`;
        taskContent.style.width = '100%';
        taskContent.style.boxSizing = 'border-box';
        taskContent.style.overflow = 'auto';
      }
    }
  };
  
  // Handle resize end
  const handleResizeEnd = () => {
    if (!isResizing) return;
    
    setIsResizing(false);
    
    // Only send the update to the server when resizing is complete
    if (onUpdate && !id.toString().startsWith('temp-')) {
      const finalSize = {
        width: taskRef.current.offsetWidth,
        height: taskRef.current.offsetHeight
      };
      
      console.log('Updating size on resize end:', finalSize);
      
      // Update via API
      if (boardId) {
        const updateSizeInBoard = async () => {
          try {
            const { getIdToken } = await import('firebase/auth');
            const { auth } = await import('../../config/firebase-config');
            
            const token = await getIdToken(auth.currentUser);
            
            // Update size via API
            await api.put(
              `/api/boards/${boardId}/components/${id}/size`,
              { size: finalSize },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            console.log('Size updated via dedicated endpoint successfully:', finalSize);
          } catch (err) {
            console.error('Error updating size via endpoint:', err);
            
            // Fallback to regular update through parent component
            console.log('Falling back to regular update method');
            onUpdate(id, { size: finalSize });
          }
        };
        
        updateSizeInBoard();
      } else {
        // If no boardId, use regular update
        onUpdate(id, { size: finalSize });
      }
    }
  };

  // Effect to update size when initialSize changes
  useEffect(() => {
    if (initialSize && !isResizing) {
      setSize(initialSize);
    }
  }, [initialSize, isResizing]);

  // Add and remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Add and remove event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  return (
    <div
      ref={taskRef}
      className={`board-component task ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="task-header">
        <div className="drag-handle"></div>
        <button className="delete-button" onClick={handleDelete}>×</button>
      </div>
      <div className="task-content">
        {tasks.map(task => (
          <div key={task.id} className="task-item">
            <input
              type="checkbox"
              checked={task.completed}
              onChange={(e) => handleTaskChange(task.id, 'completed', e.target.checked)}
            />
            <input
              type="text"
              value={task.text}
              onChange={(e) => handleTaskChange(task.id, 'text', e.target.value)}
              placeholder="Add your task here..."
              className={task.completed ? 'completed' : ''}
            />
            <button className="remove-task-button" onClick={() => removeTask(task.id)}>×</button>
          </div>
        ))}
        <button className="add-task-button" onClick={addTask}>
          + Add Task
        </button>
      </div>
      {/* Resize handle */}
      <div 
        className="resize-handle bottom-right"
        onMouseDown={handleResizeStart}
      />
    </div>
  );
};

export default Task;
